import { can, privateLabel, type BoardListing, type Viewer } from "@boem/core/shared";
import { useRef, useState } from "react";
import { boardHref, boardMark, boardMeta, boardSub, currentListing, seenBoards, unseen, useBoards } from "../boards.ts";
import { Icon } from "./Icons.tsx";
import { Popover } from "./Menu.tsx";

// The board switcher (docs/design/orgs.md 4.2): the board's name in the header is a button that lists
// the person's boards (OEMUAP, their own, the ones shared with them); a row switches this tab, its ↗
// opens the board in a new tab for Chrome's split view. The sidebar (and the ☰ drawer under 900px)
// has the same list as its Boards block. A personal board wears a lock, a "Private" chip and a band
// in the verify-queue purple, so two tabs side by side are never confused.

/** A board's mark: OE for OEMUAP, a lock for my own, the person's initials for one shared with me. */
export function OrgMark({ b }: { b: Pick<BoardListing, "kind" | "name" | "role" | "mine"> }) {
  const cls = b.kind === "company" ? "co" : b.role === "owner" ? "pe" : "sh";
  return (
    <span className={`org-ic ${cls}`} aria-hidden="true">
      {b.kind === "personal" && b.role === "owner" ? <Icon name="lock" /> : boardMark(b)}
    </span>
  );
}

/** "Private · you and Jake" beside a personal board's name. */
export function PrivateChip({ people }: { people: string[] }) {
  const label = privateLabel(people);
  return (
    <span className="org-priv" title={`${label}. Nothing on this board shows on OEMUAP.`}>
      <Icon name="lock" />
      <span className="l">{label.replace(/^Private · /, "Private · ")}</span>
    </span>
  );
}

/**
 * A board's link. The board this tab already shows goes to its main board in place (`onMain`):
 * nothing to reload, it's the same board. Another board loads fresh, so nothing of this one stays.
 */
export const boardClick = (current: boolean, onMain?: () => void) => (e: { preventDefault: () => void; button?: number; ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean }) => {
  if (!current || !onMain || e.button || e.ctrlKey || e.metaKey || e.shiftKey) return;
  e.preventDefault();
  onMain();
};

function SwitcherRow({ b, current, fresh = false, onMain }: { b: BoardListing; current: boolean; fresh?: boolean; onMain?: () => void }) {
  const href = b.exists ? boardHref(b.slug) : "/?org=me";
  return (
    <div className="osw-row" aria-current={current ? "page" : undefined}>
      <OrgMark b={b} />
      <a className="nm" role="menuitem" href={href} aria-current={current ? "page" : undefined} title={current ? "Its main board (this tab shows it)" : `Show ${b.name} in this tab`} onClick={boardClick(current, onMain)}>
        {b.name}
        {fresh && <span className="osw-dot" title="New: shared with you, not opened yet" aria-label="new" />}
      </a>
      <span className="meta">{boardMeta(b)}</span>
      {current ? (
        <span className="now">This tab</span>
      ) : (
        <a className="newtab" href={href} target="_blank" rel="noopener" aria-label={`Open ${b.name} in a new tab`} title="Open it in a new tab (put the two side by side with Chrome's split view)">
          <Icon name="goto" />
        </a>
      )}
    </div>
  );
}

/** What the switcher (and the sidebar's Boards block) lists: my boards, then the ones shared with me. */
function SwitcherList({ slug, onPeople, onSettings, onMain }: { slug: string; onPeople?: () => void; onSettings?: () => void; onMain?: () => void }) {
  const boards = useBoards();
  const list = boards?.list;
  if (!list) return <div className="osw-sec">Loading the boards&hellip;</div>;
  const here = currentListing(list, slug);
  const fresh = new Set(unseen(list, seenBoards()));
  return (
    <>
      <div className="osw-sec">Your boards</div>
      {list.boards.map((b) => (
        <SwitcherRow key={b.slug} b={b} current={b.slug === slug} onMain={onMain} />
      ))}
      {list.shared.length > 0 && (
        <>
          <div className="osw-sec">Shared with you</div>
          {list.shared.map((b) => (
            <SwitcherRow key={b.slug} b={b} current={b.slug === slug} fresh={fresh.has(b.slug) && b.slug !== slug} onMain={onMain} />
          ))}
        </>
      )}
      {onPeople && here?.kind === "personal" && (
        <button type="button" role="menuitem" className="osw-act" onClick={onPeople}>
          <Icon name="people" /> People on {here.name}&hellip;
        </button>
      )}
      {onSettings && here?.kind !== "personal" && (
        <button type="button" role="menuitem" className="osw-act" onClick={onSettings}>
          <Icon name="settings" /> Settings&hellip; <span className="osw-act-sub">GitHub</span>
        </button>
      )}
      <p className="osw-tip">
        <b>Both at once:</b> the <Icon name="goto" /> on a row opens that board in a new tab. Put the two tabs side by side with Chrome&rsquo;s split view; each keeps its own live updates.
      </p>
    </>
  );
}

/**
 * The header's board name, as the switcher: "OEMUAP ▾", or "🔒 Gautam's board ▾" on a personal
 * board. `crumb` follows it ("› 03 · Delivery app", "› Archive").
 */
export function BoardSwitcher({ crumb, crumbTitle }: { crumb?: string | null; crumbTitle?: string }) {
  const boards = useBoards();
  const onPeople = boards?.openPeople;
  const onSettings = boards?.openSettings;
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  if (!boards) return null;
  const { org } = boards;
  const toggle = () => {
    if (!open) boards.reload();
    setOpen((o) => !o);
  };
  return (
    <h1 className="osw-h1" title={crumbTitle}>
      <button type="button" ref={btn} className="osw" aria-haspopup="menu" aria-expanded={open} title="Switch board" onClick={toggle}>
        {org.kind === "personal" && <Icon name="lock" className="ic-lock" />}
        <span className="osw-name">{org.name}</span>
        <Icon name="caret" />
        {unseen(boards.list, seenBoards()).some((s) => s !== org.slug) && <span className="osw-dot" title="A board was shared with you" aria-label="a board was shared with you" />}
      </button>
      {crumb && (
        <>
          <span className="osw-crumb" aria-hidden="true">
            &rsaquo;
          </span>
          <span className="osw-proj">{crumb}</span>
        </>
      )}
      {open && (
        <Popover anchor={btn} onClose={() => setOpen(false)} label="Boards" className="osw-pop" align="start" sheetHead={<>Boards</>}>
          <SwitcherList
            slug={org.slug}
            onMain={
              boards.openMain
                ? () => {
                    setOpen(false);
                    boards.openMain!();
                  }
                : undefined
            }
            onPeople={
              onPeople
                ? () => {
                    setOpen(false);
                    onPeople();
                  }
                : undefined
            }
            onSettings={
              onSettings
                ? () => {
                    setOpen(false);
                    onSettings();
                  }
                : undefined
            }
          />
        </Popover>
      )}
    </h1>
  );
}

/** The sidebar's Boards block (in the ☰ drawer under 900px): one row per board, the current one marked. */
export function SidebarBoards() {
  const boards = useBoards();
  const list = boards?.list;
  if (!boards || !list) return null;
  const all = [...list.boards, ...list.shared];
  // only OEMUAP and nothing else to switch to: no block
  if (all.length < 2) return null;
  return (
    <>
      <div className="sb-head sb-boards-head">Boards</div>
      {all.map((b) => {
        const here = b.slug === boards.org.slug;
        return (
          <a key={b.slug} className="sb-row sb-org" href={b.exists ? boardHref(b.slug) : "/?org=me"} aria-current={here ? "page" : undefined} title={here ? `${b.name}: its main board (this tab)` : `Show ${b.name} in this tab`} onClick={boardClick(here, boards.openMain)}>
            <OrgMark b={b} />
            <span className="sb-title">{b.name}</span>
            <span className="sb-n">{boardSub(b)}</span>
          </a>
        );
      })}
      <div className="sb-sep" />
    </>
  );
}

/** A personal board with nothing on it yet (orgs.md 4.3). */
export function EmptyBoard({ viewer, onNewProject, onNewItem }: { viewer: Viewer; onNewProject: () => void; onNewItem: () => void }) {
  return (
    <div className="org-empty">
      <h2>Your board</h2>
      <p>Private to you; invite people from People. Numbers start at #1 and have nothing to do with OEMUAP&rsquo;s.</p>
      <div className="row">
        {can(viewer, "project") && (
          <button type="button" className="btn" onClick={onNewProject}>
            New project
          </button>
        )}
        {can(viewer, "create") && (
          <button type="button" className="btn primary" onClick={onNewItem}>
            New item
          </button>
        )}
      </div>
    </div>
  );
}
